import { doc, type DocumentReference, type Firestore, type Transaction } from 'firebase/firestore';
import type { Expense, TripConfig } from '../types';
import { costKindForPaymentStatus } from './bookingCostReview';
import { equal, stripUndef } from './dayWrites';
import { destinationDates, SHARED_ITEM_FIELDS } from './destinationItems';
import { readDestinationDocs } from './destinationWrites';
import { secondaryChargeRefs, sourceFieldForType, type ExpenseSourceType } from './expenseLinks';

const fields = ['amount', 'currency', 'paidStatus', 'depositAmount', 'depositCurrency'] as const;
export function linkedCostChanged(before: Expense, after: Expense): boolean {
  return fields.some(key => !equal(before[key], after[key]));
}

export function costFields(expense: Expense) {
  return stripUndef({ costAmount: expense.amount, costCurrency: expense.currency,
    costKind: costKindForPaymentStatus(expense.paidStatus), paidStatus: expense.paidStatus,
    ...(expense.paidStatus === 'Deposit paid' ? {
      depositAmount: expense.depositAmount, depositCurrency: expense.depositCurrency || expense.currency,
    } : {}),
  });
}

/** Read first and return source patches so the caller can commit them with the expense. */
export async function linkedExpenseSourceChanges(transaction: Transaction, db: Firestore, tripId: string,
  before: Expense, after: Expense, legacySource?: Record<string, unknown>): Promise<{ ref: DocumentReference; data: Record<string, unknown> }[]> {
  if ((!legacySource && (before.amountBasis !== 'full-cost' || !linkedCostChanged(before, after))) || before.itineraryDeleted || !before.sourceId) return [];
  const sourceType = before.sourceType === 'note' ? 'other' : before.sourceType as ExpenseSourceType;
  const field = sourceFieldForType(sourceType);
  if (!field) throw new Error('This expense has an unknown item link. Review it before changing the cost.');
  const tripRef = doc(db, 'trips', tripId);
  const snapshot = await transaction.get(tripRef);
  if (!snapshot.exists()) throw new Error('This trip is no longer available.');
  const trip = snapshot.data() as TripConfig;
  const expected = costFields(before);
  const next = costFields(after);
  const validateLegacy = async (items: Record<string, any>[]) => {
    if (!legacySource) return;
    for (const item of items) {
      if (!equal(item, legacySource) || (await secondaryChargeRefs(transaction, db, tripId, item, sourceType)).length) {
        throw new Error('This booking changed or has multiple linked charges. Keep its historical charges and review them together before converting to one total.');
      }
    }
  };
  const update = (item: Record<string, any>) => {
    const current = { ...item, costKind: item.costKind || costKindForPaymentStatus(item.paidStatus),
      depositCurrency: item.depositCurrency || item.costCurrency };
    if (item.expenseLogId !== before.id || (!legacySource && Object.entries(expected).some(([key, value]) => !equal(current[key], value)))) {
      throw new Error('The linked item cost changed. Reopen it and review both records before saving.');
    }
    const saved = { ...item, ...next };
    if (after.paidStatus !== 'Deposit paid') { delete saved.depositAmount; delete saved.depositCurrency; }
    return saved;
  };
  if (sourceType === 'accommodation' && trip.accommodations?.some(item => item.id === before.sourceId)) {
    await validateLegacy(trip.accommodations.filter(item => item.id === before.sourceId));
    return [{ ref: tripRef, data: { accommodations: trip.accommodations.map(item => item.id === before.sourceId ? update(item) : item) } }];
  }
  const date = before.importedItemDate || before.date;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error('The linked item date is missing. Reopen the item before editing its cost.');
  const shared = (SHARED_ITEM_FIELDS as readonly string[]).includes(field);
  const days = await readDestinationDocs(transaction, db, tripId, shared ? destinationDates(trip, date) : [date]);
  const selected = days.find(day => day.date === date);
  const found = selected?.data[field]?.some((item: { id?: string }) => item.id === before.sourceId)
    || (sourceType === 'accommodation' && selected?.data.accommodation?.id === before.sourceId);
  if (!found) throw new Error('The linked item was moved or removed. Reopen it before changing the expense.');
  await validateLegacy(days.flatMap(day => sourceType === 'accommodation' && day.data.accommodation?.id === before.sourceId
    ? [day.data.accommodation] : (day.data[field] || []).filter((item: { id?: string }) => item.id === before.sourceId)));
  return days.flatMap<{ ref: DocumentReference; data: Record<string, unknown> }>(day => {
    if (sourceType === 'accommodation' && day.data.accommodation?.id === before.sourceId) {
      return [{ ref: day.ref, data: { accommodation: update(day.data.accommodation) } }];
    }
    const items = day.data[field] as Record<string, any>[] | undefined;
    return items?.some(item => item.id === before.sourceId)
      ? [{ ref: day.ref, data: { [field]: items.map(item => item.id === before.sourceId ? update(item) : item) } }]
      : [];
  });
}
